import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "./AuthContext.jsx";
import { C } from "../theme/tokens.js";
import { requestInviteResend } from "../lib/storage.js";

// NOTE: this page deliberately renders in system-ui rather than Barlow — it is
// reached before the app shell injects the webfont. The local lbl/inp below
// therefore differ slightly from the shared ones in theme/tokens.js.

export default function Login() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | reset | resend
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // If already authenticated (either just-signed-in via onAuthStateChange or
  // navigated here while logged in), bounce to the dashboard. replace: true
  // keeps /login out of the back-button history.
  useEffect(() => {
    if (session) navigate("/", { replace: true });
  }, [session, navigate]);

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    setBusy(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
        if (error) throw error;
      } else if (mode === "resend") {
        // Deliberately shows whatever the function returns, which is the same
        // message for every address. Do not special-case "not found" here —
        // that would leak who is on the roster.
        const data = await requestInviteResend(email);
        setMsg(data?.message || "If that email is on the roster, a fresh link is on its way.");
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/callback`,
        });
        if (error) throw error;
        setMsg("Check your email for a reset link.");
      }
    } catch (err) {
      setMsg(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", background: C.shell, display: "grid", placeItems: "center",
      padding: 20, fontFamily: "system-ui, sans-serif", color: C.ink,
    }}>
      {/* boxSizing is explicit here: this page never injects the global reset
          from theme/tokens, so padding would otherwise add to the width and
          push the card past a 375px screen. */}
      <form onSubmit={submit} style={{
        background: "#fff", borderRadius: 18, padding: 32, width: "100%", maxWidth: 420,
        boxSizing: "border-box",
        boxShadow: "0 20px 60px rgba(19,34,41,0.10)", border: `1px solid ${C.line}`,
      }}>
        <div style={{ fontSize: 11, letterSpacing: ".22em", color: C.red, fontWeight: 700 }}>
          MILE MARKER
        </div>
        <h1 style={{ fontSize: 26, margin: "6px 0 4px", letterSpacing: ".01em" }}>
          {HEADING[mode]}
        </h1>
        <p style={{ fontSize: 13, color: C.sub, marginTop: 0, lineHeight: 1.5 }}>
          {BLURB[mode]}
        </p>

        <label style={lbl}>Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          style={inp} autoComplete="email" />

        {mode === "signin" && (
          <>
            <label style={lbl}>Password</label>
            <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
              style={inp} autoComplete="current-password" />
          </>
        )}

        <button type="submit" disabled={busy} style={{
          width: "100%", marginTop: 22, padding: "11px 16px", borderRadius: 9,
          background: C.red, color: "#fff", border: "none", fontWeight: 700, fontSize: 14,
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1,
        }}>
          {busy ? "…" : ACTION[mode]}
        </button>

        {msg && (
          <div style={{ marginTop: 14, fontSize: 13, lineHeight: 1.5,
            color: mode === "resend" ? C.sub : C.redDk }}>
            {msg}
          </div>
        )}

        {/* An expired invite is the single most common reason a man cannot get
            in, so it gets its own way back rather than hiding behind the
            password reset. */}
        <div style={{ marginTop: 16, display: "grid", gap: 9 }}>
          {mode !== "signin" && (
            <button type="button" onClick={() => { setMode("signin"); setMsg(""); }} style={linkBtn}>
              Back to sign in
            </button>
          )}
          {mode !== "reset" && (
            <button type="button" onClick={() => { setMode("reset"); setMsg(""); }} style={linkBtn}>
              Forgot your password?
            </button>
          )}
          {mode !== "resend" && (
            <button type="button" onClick={() => { setMode("resend"); setMsg(""); }} style={linkBtn}>
              Invite expired, or never arrived? Get a new link
            </button>
          )}
        </div>

        <div style={{ marginTop: 22, paddingTop: 14, borderTop: `1px solid ${C.line}`,
          fontSize: 11.5, color: C.faint, letterSpacing: ".02em" }}>
          Invite-only. No public signup. Equilibrium Retreat · A ministry of Men of Iron.
        </div>
      </form>
    </div>
  );
}

const HEADING = {
  signin: "Sign in",
  reset:  "Reset password",
  resend: "Get a new link",
};

const BLURB = {
  signin: "Use the email your mentor or admin invited you with.",
  reset:  "We'll email you a reset link.",
  // Says out loud the thing that actually trips men up.
  resend: "Invite links are only good for 24 hours. Put in your email and we'll send a fresh one - then open it the same day.",
};

const ACTION = {
  signin: "Sign in",
  reset:  "Send reset link",
  resend: "Send me a new link",
};

const linkBtn = {
  display: "block", background: "transparent", border: "none", textAlign: "left",
  color: C.sub, fontSize: 13, cursor: "pointer", padding: 0,
};

const lbl = { fontSize: 12.5, fontWeight: 600, color: C.sub, marginTop: 14, display: "block" };
const inp = {
  width: "100%", marginTop: 6, padding: "10px 13px", borderRadius: 9,
  border: `1.5px solid ${C.line}`, background: "#FBFBFB",
  fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box",
};
